import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
    Drawer, 
    IconButton, 
    List, 
    ListItemButton, 
    ListItemIcon, 
    ListItemText, 
    Collapse,
    Typography,
    Box
} from '@mui/material';
import { 
    Home, 
    Business, 
    ExpandLess, 
    ExpandMore, 
    Settings, 
    People,
    ChevronLeft,
    InsertDriveFile
} from '@mui/icons-material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InventoryIcon from '@mui/icons-material/Inventory';
import EngineeringIcon from '@mui/icons-material/Engineering';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import TuneIcon from '@mui/icons-material/Tune';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import RoofingIcon from '@mui/icons-material/Roofing';

interface SidebarProps {
    isOpen: boolean;    
    onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    const isActive = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setSettingsOpen(location.pathname.startsWith('/parametrages'));
    }, [location.pathname]);

    const handleMenuClick = (menuName: string) => {
        if (isMobile) {
            onClose();
        }
    };

    const handleSettingsClick = () => {
        setSettingsOpen(!settingsOpen);
    };

    const drawerContent = (
        <>
            <Box className="flex flex-col h-full bg-white">
                {/* Header */}
                <Box className="flex items-center justify-between p-4 border-b border-emerald-100">
                    <Typography variant="h6" className="text-emerald-700 font-semibold">
                        Wattless
                    </Typography>
                    {isMobile && (
                        <IconButton onClick={onClose} className="text-emerald-600">
                            <ChevronLeft />
                        </IconButton>
                    )}
                </Box>

                {/* Menu Items */}
                <List className="flex-1 px-2 py-2 space-y-1">
                    <ListItemButton
                        component={Link}
                        to="/home"
                        onClick={() => handleMenuClick('/home')}
                        selected={isActive('/home')} 
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                    >
                        <ListItemIcon>
                            <Home />
                        </ListItemIcon>
                        <ListItemText primary="Home" />
                    </ListItemButton>

                    <ListItemButton
                        component={Link}
                        to="/dpe"
                        onClick={() => handleMenuClick('/dpe')}
                        selected={isActive('/dpe')} 
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                    >
                        <ListItemIcon>
                            <InsertDriveFile />
                        </ListItemIcon>
                        <ListItemText primary="Gestion DPE" />
                    </ListItemButton>

                    <ListItemButton
                        component={Link}
                        to="/projects"
                        onClick={() => handleMenuClick('/projects')}
                        selected={isActive('/projects')} 
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                    >
                        <ListItemIcon>
                            <Business />
                        </ListItemIcon>
                        <ListItemText primary="Gestion projets" />
                    </ListItemButton>

                    <ListItemButton
                        component={Link}
                        to="/planactions"
                        onClick={() => handleMenuClick('/planactions')}
                        selected={isActive('/planactions')} 
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                    >
                        <ListItemIcon>
                            <HomeWorkIcon />
                        </ListItemIcon>
                        <ListItemText primary="Gestion plans d'actions" />
                    </ListItemButton>

                    <ListItemButton
                        component={Link}
                        to="/abaques"
                        onClick={() => handleMenuClick('/abaques')}
                        selected={isActive('/abaques')} 
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                    >
                        <ListItemIcon>
                            <MenuBookIcon />
                        </ListItemIcon>
                        <ListItemText primary="Gestion abaques" />
                    </ListItemButton>

                    <ListItemButton
                        component={Link}
                        to="/fournisseurs"
                        onClick={() => handleMenuClick('/fournisseurs')}
                        selected={isActive('/fournisseurs')} 
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                    >
                        <ListItemIcon>
                            <EngineeringIcon />
                        </ListItemIcon>
                        <ListItemText primary="Gestion fournisseurs" />
                    </ListItemButton>

                    <ListItemButton
                        component={Link}
                        to="/catalogues"
                        onClick={() => handleMenuClick('/catalogues')}
                        selected={isActive('/catalogues')} 
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                    >
                        <ListItemIcon>
                            <InventoryIcon />
                        </ListItemIcon>
                        <ListItemText primary="Gestion catalogues" />
                    </ListItemButton>

                    <ListItemButton
                        onClick={() => alert('Cette fonctionnalitée sera bientôt disponible')}
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                        selected={isActive('/home')} 
                    >
                        <ListItemIcon>
                            <People />
                        </ListItemIcon>
                        <ListItemText primary="Gestion utilisateurs" />
                    </ListItemButton>

                    <ListItemButton
                        onClick={() => alert('Cette fonctionnalitée sera bientôt disponible')}
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                        selected={isActive('/home')} 
                    >
                        <ListItemIcon>
                            <People />
                        </ListItemIcon>
                        <ListItemText primary="Gestion clients" />
                    </ListItemButton>

                    {/* Menu Paramétrages */}
                    <ListItemButton
                        onClick={handleSettingsClick}
                        className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                        selected={isActive('/home')} 
                    >
                        <ListItemIcon>
                            <Settings />
                        </ListItemIcon>
                        <ListItemText primary="Paramétrages" />
                        {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                        <List component="div" className="pl-4 space-y-1">
                            <ListItemButton
                                component={Link}
                                to="/parametrages/roles"
                                onClick={() => handleMenuClick('/parametrages/roles')}
                                selected={isActive('/parametrages/roles')} 
                                className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                            >
                                <ListItemIcon>
                                    <ManageAccountsIcon />
                                </ListItemIcon>
                                <ListItemText primary="Rôles" />
                            </ListItemButton>

                            <ListItemButton
                                onClick={() => alert('Cette fonctionnalitée sera bientôt disponible')}
                                className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                                selected={isActive('/home')} 
                            >
                                <ListItemIcon>
                                    <TuneIcon />
                                </ListItemIcon>
                                <ListItemText primary="Préferences utilisateurs" />
                            </ListItemButton>

                            <ListItemButton
                                onClick={() => alert('Cette fonctionnalitée sera bientôt disponible')}
                                className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                                selected={isActive('/home')} 
                            >
                                <ListItemIcon>
                                    <RoofingIcon />
                                </ListItemIcon>
                                <ListItemText primary="Sociétés" />
                            </ListItemButton>

                            <ListItemButton
                                onClick={() => alert('Cette fonctionnalitée sera bientôt disponible')}
                                className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                                selected={isActive('/home')} 
                            >
                                <ListItemIcon>
                                    <LocalOfferIcon />
                                </ListItemIcon>
                                <ListItemText primary="Offre du moment" />
                            </ListItemButton>

                            <ListItemButton
                                component={Link}
                                to="/parametrages/monitoring"
                                onClick={() => handleMenuClick('/parametrages/monitoring')}
                                selected={isActive('/parametrages/monitoring')} 
                                className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                            >
                                <ListItemIcon>
                                    <QueryStatsIcon />
                                </ListItemIcon>
                                <ListItemText primary="Monitoring" />
                            </ListItemButton>

                            <ListItemButton
                                onClick={() => alert('Cette fonctionnalitée sera bientôt disponible')}
                                className={`rounded-lg text-gray-600 hover:bg-emerald-50/50`}
                                selected={isActive('/home')}
                            >
                                <ListItemIcon>
                                    <MultipleStopIcon />
                                </ListItemIcon>
                                <ListItemText primary="Connecteurs" />
                            </ListItemButton>
                        </List>
                    </Collapse>
                </List>
            </Box>
        </>
    );

    return (
        <>
            {/* Version mobile */}
            {isMobile ? (
                <Drawer
                    anchor="left"
                    open={isOpen}
                    onClose={onClose}
                    variant="temporary"
                    PaperProps={{
                        className: "w-72"
                    }}
                >
                    {drawerContent}
                </Drawer>
            ) : (
                /* Version desktop */
                <Drawer
                    variant="permanent"
                    open={isOpen}
                    PaperProps={{
                        className: `${isOpen ? 'w-72' : 'w-16'} transition-all duration-300`,
                        sx: {
                            overflow: 'hidden',
                            border: 'none',
                            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
                        }
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
        </>
    );
};

export default Sidebar;
